import { graphql } from '@apollo/client/react/hoc';
import * as _ from 'lodash-es';
import { withRouter } from 'react-router-dom';
import { branch, compose, mapProps, withProps } from 'recompose';
import {
  LEAGUE_BASICS_QUERY,
  ORGANIZATION_LIST_QUERY,
  ORGANIZATION_SPORT_QUERY,
} from '../HOMEPAGE_QUERIES';
import SportPagePresenter from './SportPagePresenter';
import { Loading } from '../../../../components';
import ErrorPage from '../../../../pages/ErrorPage';
import { omitDeepTypename } from '../../../../utils/pass-graphql';
import { dashSpaceSwap } from '../../../../utils/url-search-helpers';
import { Activities } from '../../../../shared/activity-name-enum';
import { LeagueStageEnum } from '../../../../shared/league-status-enum';

export default compose(
  withRouter,
  withProps(({ match }) => ({
    chosenSport: dashSpaceSwap(_.get(match, 'params.sportName'), 'toSpace'),
    chosenCity: dashSpaceSwap(_.split(_.get(match, 'url'), '/')[1] || '', 'toSpace'),
  })),
  branch(
    ({ chosenSport }) => !_.find(Activities, { name: chosenSport }),
    () => ErrorPage
  ),
  graphql(ORGANIZATION_LIST_QUERY, {
    skip: ({ chosenCity }) => !chosenCity,
    options: () => ({
      fetchPolicy: 'network-only',
    }),
    props: ({ data: { loading, organizationList = [] }, ownProps: { chosenCity } }) => {
      const foundCity = organizationList?.find(({ name }) => name === chosenCity);
      return {
        organizationListLoading: loading,
        cityOrgId: foundCity?._id,
      };
    },
  }),
  branch(
    ({ organizationListLoading }) => organizationListLoading,
    () => Loading
  ),
  graphql(ORGANIZATION_SPORT_QUERY, {
    skip: ({ cityOrgId }) => !cityOrgId,
    options: ({ cityOrgId }) => ({
      fetchPolicy: 'network-only',
      variables: {
        organizationId: cityOrgId,
        getVoloDetails: true,
      },
    }),
    props: ({ data: { loading, organization }, ownProps: { chosenSport } }) => {
      const levels = organization?.homepageDetails?.sportDefaults?.competitionLevels || [];
      const types = organization?.homepageDetails?.sportDefaults?.programTypes || [];
      const allSports = organization?.homepageDetails?.sportDetails || [];
      const lowerSport = chosenSport.toLowerCase();
      return {
        organizationSportLoading: loading,
        homepageDefaults: {
          programDescriptions: types.reduce(
            (acc, { name, description }) => ({ ...acc, [name]: description }),
            {}
          ),
          programImages: types.reduce(
            (acc, { name, image }) => ({ ...acc, [name]: image?.url || '' }),
            {}
          ),
          competitionLevels: levels.reduce(
            (acc, { level, description }) => ({ ...acc, [level]: description }),
            {}
          ),
        },
        sportDetails: omitDeepTypename(
          allSports.filter(s => s.sportName.toLowerCase() === lowerSport)[0] || {}
        ),
        sportVenues: omitDeepTypename(
          (organization?.approvedVenues || []).filter(
            ({ sportDetails, disabled }) =>
              !disabled && sportDetails.some(s => s.sportName === chosenSport)
          ) || []
        ),
      };
    },
  }),
  graphql(LEAGUE_BASICS_QUERY, {
    skip: ({ chosenCity }) => !chosenCity,
    options: ({ cityOptions, chosenCity, chosenSport }) => ({
      fetchPolicy: 'network-only',
      variables: {
        input: {
          organizationId: (cityOptions || []).find(({ label }) => label === chosenCity)?.value,
          sport: chosenSport,
          stage: LeagueStageEnum.UPCOMING,
          omitReoccuringParents: false,
          pagination: {
            numPerPage: 1000,
            pageNum: 1,
          },
          discoverable: true,
          omitArchived: true,
        },
      },
    }),
    props: ({ data: { loading, leagueList } }) => ({
      programsLoading: loading,
      upcomingProgramTypes: _.map(_.get(leagueList, 'leagues'), l => l.programType) || [],
    }),
  }),
  branch(
    ({ organizationSportLoading, programsLoading }) => organizationSportLoading || programsLoading,
    () => Loading
  ),
  mapProps(
    ({
      chosenCity,
      chosenSport,
      homepageDefaults,
      sportDetails,
      sportVenues,
      upcomingProgramTypes,
    }) => ({
      chosenCity,
      chosenSport,
      homepageDefaults,
      sportDetails: {
        ...sportDetails,
        notes: _.get(sportDetails, 'notes') || '',
        faq: sportDetails?.faq ? _.compact(sportDetails?.faq) : [],
      },
      sportVenues,
      upcomingProgramTypes,
    })
  )
)(SportPagePresenter);
